import { getReviewByOrder, listReviews } from '@lib/util/reviews-handler'
import { Customer, Order, Product } from '@medusajs/medusa'
import { QueryObserverResult, useQuery } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { useEffect, useState } from 'react'
import { Media } from 'types/global'

export interface Review {
  id: string
  name: string
  stars: number
  content: string
  medias: Array<Media>
  approved: boolean
  product_id: string
  product: Product
  customer_id: string
  customer: Customer
  order_id: string
  order: Order
  created_at: string
  updated_at: string
  variant?: string
}

interface ReviewsResponse {
  reviews: Review[]
  total: number
}

export function useReviews({
  productId,
  limit = 10,
  currentPage = 1
}: {
  productId: string
  limit?: number
  currentPage?: number
}): {
  reviews: Review[]
  total: number
  isLoading: boolean
} {
  const [reviews, setReviews] = useState<Review[]>([])
  const [total, setTotal] = useState<number>(0)
  const { data, isLoading } = useQuery<ReviewsResponse, any>(
    [`get-reviews-${productId}-${limit}-${currentPage}`],
    () => listReviews(productId, limit, currentPage)
  )

  useEffect(() => {
    if (!data) {
      setReviews([])
      setTotal(0)
    } else {
      const variants = data.reviews.map((review) => {
        const orderItems = review.order.items
        const product = orderItems.find(
          (item) => item.variant.product_id === productId
        )
        return product?.variant.title
      })

      setReviews(
        data.reviews.map((review, index) => ({
          ...review,
          variant: variants[index]
        }))
      )
      setTotal(data.total)
    }
  }, [data])

  return { reviews, total, isLoading }
}

export function useReviewsByOrder(orderId: string): {
  reviews: Review[]
  isLoading: boolean
  refetchReviews: () => Promise<
    QueryObserverResult<AxiosResponse<Review[], any>, unknown>
  >
} {
  const [reviews, setReviews] = useState<Review[]>([])

  const { isLoading, refetch } = useQuery<AxiosResponse<Review[], any>>(
    [`get-review-by-order-${orderId}`],
    () => getReviewByOrder(orderId),
    {
      onSuccess: (response) => {
        setReviews(response.data)
      }
    }
  )

  return { reviews, isLoading, refetchReviews: refetch }
}
